import { useAuth } from './useAuth';
import {
  UserRole,
  isAdmin,
  isInstructor,
  isPremium,
  hasSubscription,
  isManagerRole,
} from '@shared/auth-simple';

/**
 * SIMPLIFIED Authorization hook - role-based checks only
 * Replaces complex permission system with simple role checks
 */
export function useAuthz() {
  const { user } = useAuth();

  /**
   * Check if user has admin role
   */
  const isUserAdmin = (): boolean => {
    if (!user?.role) return false;
    return isAdmin(user.role as UserRole);
  };

  /**
   * Check if user has instructor role or higher
   */
  const isUserInstructor = (): boolean => {
    if (!user?.role) return false;
    return isInstructor(user.role as UserRole);
  };

  /**
   * BRITE: check if user is manager tier (stored 'instructor') or admin.
   * Use for: intake review, PIN issuance, events, Generator, user list.
   */
  const isUserManager = (): boolean => {
    if (!user?.role) return false;
    return isManagerRole(user.role as UserRole);
  };

  /**
   * Check if user has premium membership (premium, instructor, admin)
   * Use for: Platform features like library, avatars, chamber of echoes
   */
  const isUserPremium = (): boolean => {
    if (!user?.role) return false;
    return isPremium(user.role as UserRole);
  };

  /**
   * DEPRECATED: Use isPremium for platform features
   * Check if user has subscription (subscriber, instructor, or admin)
   */
  const hasUserSubscription = (): boolean => {
    if (!user?.role) return false;
    return hasSubscription(user.role as UserRole);
  };

  /**
   * Generic role check - kept for backward compatibility
   */
  const canSync = (roleOrCheck: string | UserRole): boolean => {
    if (!user?.role) return false;

    // Map old permission checks to role checks
    // This is a compatibility shim for code not yet migrated
    if (typeof roleOrCheck === 'string') {
      // Map common permission strings to role checks
      const permissionMap: Record<string, boolean> = {
        MANAGE_SYSTEM_SETTINGS: isUserAdmin(),
        MANAGE_USERS: isUserAdmin(),
        CREATE_COURSES: isUserInstructor(),
        EDIT_ANY_COURSE: isUserInstructor(),
        DELETE_COURSES: isUserAdmin(),
        MANAGE_CONTENT: isUserInstructor(),
        EDIT_ANY_PROFILE: isUserAdmin(),
        VIEW_CHAMBER_OF_ECHOES: hasUserSubscription(),
        PROVIDE_COUNSELING: isUserInstructor(),
        VIEW_COURSE_CONTENT: hasUserSubscription(),
        VIEW_OWN_PROGRESS: hasUserSubscription(),
        SEND_MESSAGES: hasUserSubscription(),
      };
      return permissionMap[roleOrCheck] || false;
    }

    return user.role === roleOrCheck;
  };

  return {
    canSync,
    isAdmin: isUserAdmin,
    isInstructor: isUserInstructor,
    isManager: isUserManager,
    isPremium: isUserPremium,
    hasSubscription: hasUserSubscription, // DEPRECATED
  };
}

/**
 * Convenience helper for common role checks
 * SIMPLIFIED - no more complex permissions
 */
export const useCommonPermissions = () => {
  const { isAdmin, isInstructor, isManager, hasSubscription } = useAuthz();

  return {
    canManageSystem: isAdmin,
    canCreateCourses: isInstructor,
    canEditAnyCourse: isInstructor,
    canDeleteCourses: isAdmin,
    canManageContent: isInstructor,
    canEditAnyProfile: isAdmin,
    canViewChamberOfEchoes: hasSubscription,
    canProvideCounseling: isInstructor,
    canViewCourseContent: hasSubscription,
    canViewOwnProgress: hasSubscription,
    canSendMessages: hasSubscription,
    // BRITE manager-tier permissions
    canManageIntake: isManager,
    canIssuePins: isManager,
    canViewUserList: isManager,
    canAccessGenerator: isManager,
  };
};
